// frontend/app/components/lessonPlans/LessonUploadButton.js
//
// "Upload lesson (.json)" + "Download example" as one compact row, used by the
// admin lesson library. The teacher library uses StartLessonCard instead; both
// are driven by useLessonUpload.
//
// Web only. A file picker needs a DOM <input type="file">, which native has no
// equivalent of, so on native the row is not rendered rather than shown dead.

import React from "react";
import {
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { getLessonColors } from "./lessonPlanTheme";
import useLessonUpload, {
  EXAMPLE_FILE_NAME,
  MAX_LESSON_FILE_BYTES,
} from "./useLessonUpload";
import { HiddenLessonFileInput, LessonUploadErrorPanel } from "./LessonUploadParts";

export { EXAMPLE_FILE_NAME, MAX_LESSON_FILE_BYTES };

const LessonUploadButton = ({
  fetchWithAuth,
  onImported,
  isDarkMode = false,
  t = (s) => s,
  style,
}) => {
  const upload = useLessonUpload({ fetchWithAuth, onImported, t });

  if (Platform.OS !== "web") return null;

  const colors = getLessonColors(isDarkMode);

  return (
    <View style={style}>
      <View style={styles.row}>
        <TouchableOpacity
          testID="lesson-upload-button"
          onPress={upload.openPicker}
          disabled={upload.busy}
          accessibilityRole="button"
          accessibilityLabel={t("Upload lesson (.json)")}
          style={[
            styles.button,
            { borderColor: colors.accentPrimary },
            upload.busy && styles.busy,
          ]}
        >
          <MaterialCommunityIcons
            name="file-upload-outline"
            size={16}
            color={colors.accentPrimary}
          />
          <Text style={[styles.buttonText, { color: colors.accentPrimary }]}>
            {upload.busy ? t("Uploading…") : t("Upload lesson (.json)")}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          testID="lesson-example-download"
          onPress={upload.downloadExample}
          disabled={upload.downloading}
          accessibilityRole="button"
          accessibilityLabel={t("Download example")}
          style={[styles.link, upload.downloading && styles.busy]}
        >
          <MaterialCommunityIcons
            name="download-outline"
            size={16}
            color={colors.accentPrimary}
          />
          <Text style={[styles.linkText, { color: colors.accentPrimary }]}>
            {t("Download example")}
          </Text>
        </TouchableOpacity>
      </View>
      <HiddenLessonFileInput inputRef={upload.inputRef} onChange={upload.handleFile} />
      <LessonUploadErrorPanel
        errors={upload.errors}
        onDismiss={upload.clearErrors}
        colors={colors}
        t={t}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  button: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  buttonText: { fontSize: 12, fontWeight: "700" },
  link: { flexDirection: "row", alignItems: "center", gap: 4 },
  linkText: { fontSize: 12, fontWeight: "700" },
  busy: { opacity: 0.6 },
});

export default LessonUploadButton;
